import { ParamMap } from '@angular/router';
import { LocalDate } from '../../core/model/local-date';
import { filterStateSignature, readIntegerParam, readLocalDateParam, withIgnoreSaveOnLoad } from '../../core/service/list-query-params';

export interface VacantListDrilldownQuery {
  vacantPeriod?: number;
  vacantOnDate?: LocalDate;
  blockedRentalObjects?: number;
  selectedFields: string[];
  isExternal: boolean;
}

export function parseVacantListDrilldownQuery(params: ParamMap): VacantListDrilldownQuery {
  const vacantPeriod = readIntegerParam(params, 'vacantPeriod', 0, 2);
  const vacantOnDate = readLocalDateParam(params, 'vacantOnDate');
  const blockedRentalObjects = readIntegerParam(params, 'blockedRentalObjects', 0, 2);
  const selectedFields: string[] = [];

  if (vacantPeriod !== undefined) selectedFields.push('vacantPeriod');
  if (vacantOnDate !== undefined) selectedFields.push('vacantOnDate');
  if (blockedRentalObjects !== undefined) selectedFields.push('blockedRentalObjects');

  return {
    vacantPeriod,
    vacantOnDate,
    blockedRentalObjects,
    selectedFields,
    isExternal: selectedFields.length > 0,
  };
}

export class VacantListDrilldownState {
  private query: VacantListDrilldownQuery = { selectedFields: [], isExternal: false };
  private ignoreSaveOnLoad = false;
  private initialFilterSignature: string | null = null;

  setQuery(params: ParamMap): void {
    this.query = parseVacantListDrilldownQuery(params);
    this.ignoreSaveOnLoad = this.query.isExternal;
    this.initialFilterSignature = null;
  }

  applyToDefinition<T extends Record<string, any>>(
    definition: T
  ): T & { vacantPeriod?: number; vacantOnDate?: LocalDate | null; blockedRentalObjects?: number; selectedFields?: string[] } {
    if (!this.query.isExternal) return definition;
    return {
      ...definition,
      vacantPeriod: this.query.vacantPeriod ?? 0,
      vacantOnDate: this.query.vacantOnDate ?? null,
      blockedRentalObjects: this.query.blockedRentalObjects ?? 0,
      selectedFields: [...this.query.selectedFields],
    };
  }

  prepareFilter<T extends Record<string, any>>(filter: T): T & { ignoreSaveOnLoad: boolean } {
    if (this.ignoreSaveOnLoad && Array.isArray(filter?.selectedFields)) {
      const signature = filterStateSignature(filter);
      if (this.initialFilterSignature === null) this.initialFilterSignature = signature;
      else if (signature !== this.initialFilterSignature) this.ignoreSaveOnLoad = false;
    }
    return withIgnoreSaveOnLoad(filter, this.ignoreSaveOnLoad);
  }

  get externalQueryActive(): boolean {
    return this.ignoreSaveOnLoad;
  }
}
